/**
 * Post-trip survey: questions are data (edited in Admin > Trip surveys), so
 * everything here works from the question list, never from fixed fields.
 */

export type QuestionKind = 'stars' | 'scale10' | 'choice' | 'multi' | 'yesno' | 'text';
export type QuestionRole = 'overall' | 'host_rating' | 'nps' | 'trouble' | 'share_with_host';

export interface QuestionOption { key: string; label: string }

export interface SurveyQuestion {
  id: string;
  kind: QuestionKind;
  label: string;
  hint: string | null;
  options: QuestionOption[];
  required: boolean;
  section: string;
  followup: { on: string[]; placeholder?: string } | null;
  role: QuestionRole | null;
}

/** An answer in progress: `v` is the value, `note` the follow-up text. */
export interface AnswerEntry { v?: string | number | boolean | string[]; note?: string }
export type AnswerMap = Record<string, AnswerEntry>;

export const KIND_LABEL: Record<QuestionKind, string> = {
  stars: 'Stars (1–5)',
  scale10: 'Scale (0–10)',
  choice: 'Pick one',
  multi: 'Pick several',
  yesno: 'Yes / No',
  text: 'Short text',
};

/** "{host}" and "{trip}" in a question become the host's first name and the trip's title, in quotes. */
export function fill(text: string, vars: { host?: string | null; trip?: string | null }): string {
  return text
    .replace(/\{host\}/g, vars.host || 'your host')
    .replace(/\{trip\}/g, vars.trip ? `“${vars.trip}”` : 'your trip');
}

/** The key a yes/no answer maps to in the question's options. */
export function answerKeys(q: SurveyQuestion, entry: AnswerEntry | undefined): string[] {
  const v = entry?.v;
  if (v === undefined) return [];
  if (q.kind === 'yesno') return [v === true ? 'yes' : 'no'];
  if (q.kind === 'choice') return [String(v)];
  if (q.kind === 'multi') return Array.isArray(v) ? v : [];
  return [];
}

/** Does the follow-up text box show under this question right now? */
export function followupOpen(q: SurveyQuestion, entry: AnswerEntry | undefined): boolean {
  if (!q.followup) return false;
  const keys = answerKeys(q, entry);
  if (keys.length === 0) return false;
  return q.followup.on.includes('*') || keys.some((k) => q.followup!.on.includes(k));
}

export function isAnswered(q: SurveyQuestion, entry: AnswerEntry | undefined): boolean {
  const v = entry?.v;
  if (v === undefined || v === null) return false;
  if (q.kind === 'text') return typeof v === 'string' && v.trim() !== '';
  if (q.kind === 'multi') return Array.isArray(v) && v.length > 0;
  return true;
}

/** Labels of required questions still unanswered. */
export function missingRequired(questions: SurveyQuestion[], answers: AnswerMap): string[] {
  return questions.filter((q) => q.required && !isAnswered(q, answers[q.id])).map((q) => q.label);
}

/** What gets sent: only answered questions, text trimmed, notes only where the follow-up is open. */
export function toPayload(questions: SurveyQuestion[], answers: AnswerMap): Record<string, { v: unknown; note?: string }> {
  const out: Record<string, { v: unknown; note?: string }> = {};
  for (const q of questions) {
    const entry = answers[q.id];
    if (!isAnswered(q, entry)) continue;
    const item: { v: unknown; note?: string } = { v: q.kind === 'text' ? String(entry!.v).trim().slice(0, 1000) : entry!.v };
    const note = entry!.note?.trim();
    if (note && followupOpen(q, entry)) item.note = note.slice(0, 2000);
    out[q.id] = item;
  }
  return out;
}

/** Recommend score: % who gave 9–10 minus % who gave 0–6, from -100 to 100. */
export function npsScore(scores: number[]): number | null {
  if (scores.length === 0) return null;
  const promoters = scores.filter((s) => s >= 9).length;
  const detractors = scores.filter((s) => s <= 6).length;
  return Math.round(((promoters - detractors) / scores.length) * 100);
}

export function average(values: (number | null | undefined)[]): number | null {
  const nums = values.filter((v): v is number => typeof v === 'number');
  if (nums.length === 0) return null;
  return Math.round((nums.reduce((s, n) => s + n, 0) / nums.length) * 10) / 10;
}

// ── Admin results ────────────────────────────────────────────────────────────

export interface AdminQuestion extends SurveyQuestion {
  position: number;
  archived_at: string | null;
  answer_count: number;
  /** Shown to the host (only for travelers who agreed to share). Locked once answered. */
  host_visible: boolean;
}

export interface AdminAnswer { question_id: string; label: string; value: unknown; note: string | null }
export interface AdminSurvey {
  id: string;
  completed_at: string;
  host_id: string;
  host_name: string | null;
  traveler_name: string | null;
  trip_name: string | null;
  answers: AdminAnswer[];
}

export interface QuestionSummary {
  question: AdminQuestion;
  count: number;
  /** stars / scale10 */
  average?: number | null;
  nps?: number | null;
  /** choice / multi / yesno: how many picked each option label. */
  counts?: { label: string; n: number }[];
  /** text: the latest answers. */
  texts?: string[];
}

/** One summary per question that has answers, in question order (archived ones included). */
export function summarize(questions: AdminQuestion[], surveys: AdminSurvey[]): QuestionSummary[] {
  const byQuestion = new Map<string, AdminAnswer[]>();
  for (const s of surveys) for (const a of s.answers) byQuestion.set(a.question_id, [...(byQuestion.get(a.question_id) ?? []), a]);

  const out: QuestionSummary[] = [];
  for (const q of questions) {
    const answers = byQuestion.get(q.id) ?? [];
    if (answers.length === 0) continue;
    const summary: QuestionSummary = { question: q, count: answers.length };
    if (q.kind === 'stars' || q.kind === 'scale10') {
      const nums = answers.map((a) => Number(a.value)).filter((n) => Number.isFinite(n));
      summary.average = average(nums);
      if (q.role === 'nps') summary.nps = npsScore(nums);
    } else if (q.kind === 'text') {
      summary.texts = answers.map((a) => String(a.value)).slice(0, 20);
    } else {
      const tally = new Map<string, number>();
      for (const a of answers) {
        const picked = q.kind === 'yesno' ? [a.value === true ? 'yes' : 'no'] : q.kind === 'multi' ? (a.value as string[]) : [String(a.value)];
        for (const k of picked) tally.set(k, (tally.get(k) ?? 0) + 1);
      }
      const label = (k: string) => q.options.find((o) => o.key === k)?.label ?? k;
      // Most picked first; equal counts keep the question's own option order.
      const order = (k: string) => { const i = q.options.findIndex((o) => o.key === k); return i === -1 ? q.options.length : i; };
      summary.counts = [...tally.entries()]
        .sort((a, b) => b[1] - a[1] || order(a[0]) - order(b[0]))
        .map(([k, n]) => ({ label: label(k), n }));
    }
    out.push(summary);
  }
  return out;
}

/** Surveys where the traveler said something went wrong (the "trouble" core question). */
export function troubleReports(questions: AdminQuestion[], surveys: AdminSurvey[]) {
  const trouble = questions.find((q) => q.role === 'trouble');
  if (!trouble) return [];
  return surveys.flatMap((s) => {
    const a = s.answers.find((x) => x.question_id === trouble.id);
    return a && a.value === true ? [{ survey: s, note: a.note }] : [];
  });
}

// ── Admin editor rules (the database enforces the same) ──────────────────────

export type QuestionAction = 'edit' | 'delete' | 'replace' | 'archive' | 'restore';

/**
 * What an admin may do with a question, given whether it has answers and whether it is core.
 * A core question can't be archived or replaced (the rest of the system relies on it), so
 * once it has answers its wording is locked.
 */
export function allowedActions(q: Pick<AdminQuestion, 'role' | 'answer_count' | 'archived_at'>): QuestionAction[] {
  if (q.archived_at) return ['restore'];
  const used = q.answer_count > 0;
  if (q.role) return used ? [] : ['edit'];
  return used ? ['replace', 'archive'] : ['edit', 'delete'];
}

/** A key for a new option from its label: "Great value" -> "great_value". */
export function optionKey(label: string, taken: string[]): string {
  const base = label.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '') || 'option';
  let key = base;
  for (let n = 2; taken.includes(key); n++) key = `${base}_${n}`;
  return key;
}
